import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { ReviewDto, ReviewStatus } from '@ecom/contracts';
import { FilterBar, FilterField, ToggleGroup } from '@/components/filters';
import { ProductPicker } from '@/components/product-picker';
import { can } from '@/lib/session';
import { useAuthStore } from '@/stores/auth.store';
import { EMPTY_REVIEW_FILTERS, useReviewsStore, type ReviewBulkOutcome } from '@/stores/reviews.store';
import { useWorkspaceStore } from '@/stores/workspace.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, date } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import { CheckIcon } from '@/components/icons';
import { ListCard, ListToolbar, LoadMore, Page, TableScroll, Tabs, Thumb } from '@/components/page';
import {
  Alert,
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Input,
  Pill,
  Skeleton,
  Textarea,
  cx,
  type PillTone,
} from '@/components/ui';

const STATUSES: { value: ReviewStatus; key: 'pendingQueue' | 'approved' | 'rejected'; tone: PillTone }[] = [
  { value: 'PENDING', key: 'pendingQueue', tone: 'warning' },
  { value: 'APPROVED', key: 'approved', tone: 'success' },
  { value: 'REJECTED', key: 'rejected', tone: 'danger' },
];

/** A tab is one status, or all of them; a hand-made mix from the filters is `custom` — no tab lit. */
type ReviewView = ReviewStatus | 'all' | 'custom';

type Decision = Extract<ReviewStatus, 'APPROVED' | 'REJECTED'>;

function viewOf(statuses: ReviewStatus[]): ReviewView {
  if (statuses.length === 0) return 'all';
  return statuses.length === 1 ? statuses[0] : 'custom';
}

/**
 * Review moderation.
 *
 * Rendered as cards rather than a table: the reviewer's actual words are the
 * thing being judged, and a table row truncates exactly what a moderator needs
 * to read. Each card says which product it is about, and a selection publishes
 * or rejects several at once.
 */
export function ReviewsPage() {
  const { items, loading, loadingMore, bulkSaving, error, nextCursor, filters, total, productName, load, loadMore, moderateMany } =
    useReviewsStore();
  const pending = useWorkspaceStore((state) => state.attention.reviewsPending);
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  useEffect(() => {
    void load();
  }, [load, locale]);

  // ─── Selection ─────────────────────────────────────────────────────────────
  // Held here, not in the store, as on the orders list: it is what this screen
  // is pointing at, and a new filter starts it again.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkTarget, setBulkTarget] = useState<Decision | null>(null);
  const [bulkOutcome, setBulkOutcome] = useState<ReviewBulkOutcome | null>(null);

  useEffect(() => {
    setSelected(new Set());
    setBulkOutcome(null);
  }, [filters]);

  // A review moderated out of the view leaves the selection with it.
  const chosen = useMemo(() => items.filter((review) => selected.has(review.id)), [items, selected]);
  const allShown = items.length > 0 && items.every((review) => selected.has(review.id));
  const toChange = bulkTarget ? chosen.filter((review) => review.status !== bulkTarget) : [];

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  async function applyBulk() {
    if (!bulkTarget) return;
    const result = await moderateMany(
      toChange.map((review) => review.id),
      bulkTarget,
    );
    setBulkOutcome(result);
    setBulkTarget(null);
    setSelected(new Set(result.failed.map((failure) => failure.id)));
  }

  const stars = (value: string) => count(Number(value), locale);

  return (
    <Page title={t('reviews')} subtitle={t('reviewsSubtitle')} fill>
      {bulkOutcome ? (
        <Alert tone={bulkOutcome.failed.length > 0 ? 'error' : 'success'}>
          <span className="block">
            {bulkOutcome.failed.length > 0
              ? t('bulkResult', { done: count(bulkOutcome.done, locale), failed: count(bulkOutcome.failed.length, locale) })
              : t('reviewsBulkDone', { done: count(bulkOutcome.done, locale) })}
          </span>
          {bulkOutcome.failed.map((failure) => (
            <span key={failure.id} className="block text-xs">
              {failure.label}: {failure.message}
            </span>
          ))}
        </Alert>
      ) : null}

      <ListCard>
        {/* The status views up front; the filters below still take a mix. */}
        <Tabs<ReviewView>
          className="px-2"
          label={t('reviews')}
          value={viewOf(filters.statuses)}
          onChange={(id) => void load({ statuses: id === 'all' || id === 'custom' ? [] : [id] })}
          items={[
            ...STATUSES.map((entry) => ({
              id: entry.value,
              label: t(entry.key),
              // The menu's own count of what is waiting, so the tab and the badge agree.
              count: entry.value === 'PENDING' && pending !== undefined ? count(pending, locale) : undefined,
            })),
            { id: 'all' as const, label: t('allReviews') },
          ]}
        />
        <ListToolbar>
          <FilterBar
            search={filters.search}
            onSearch={(search) => void load({ search })}
            placeholder={t('searchReviewsHint')}
            total={total ?? undefined}
            shown={items.length}
            chips={[
              ...filters.statuses.map((value) => ({
                key: `s-${value}`,
                label: t(STATUSES.find((entry) => entry.value === value)?.key ?? 'pendingQueue'),
                onRemove: () =>
                  void load({ statuses: filters.statuses.filter((entry) => entry !== value) }),
              })),
              ...(filters.productId
                ? [
                    {
                      key: 'product',
                      label: productName ? `${t('product')}: ${productName}` : t('product'),
                      onRemove: () => void load({ productId: '' }),
                    },
                  ]
                : []),
              ...(filters.minRating
                ? [
                    {
                      key: 'min',
                      label: `${t('minRating')}: ${stars(filters.minRating)}`,
                      onRemove: () => void load({ minRating: '' }),
                    },
                  ]
                : []),
              ...(filters.maxRating
                ? [
                    {
                      key: 'max',
                      label: `${t('maxRating')}: ${stars(filters.maxRating)}`,
                      onRemove: () => void load({ maxRating: '' }),
                    },
                  ]
                : []),
            ]}
            onClearAll={() => void load(EMPTY_REVIEW_FILTERS)}
          >
            <div className="grid gap-4 md:grid-cols-2">
              <FilterField label={t('filterStatus')}>
                <ToggleGroup
                  options={STATUSES.map((entry) => ({ value: entry.value, label: t(entry.key) }))}
                  selected={filters.statuses}
                  onChange={(statuses) => void load({ statuses: statuses as ReviewStatus[] })}
                />
              </FilterField>

              {/* One star to two is the queue worth reading first. */}
              <div className="grid grid-cols-2 gap-2">
                <FilterField label={t('minRating')} htmlFor="rv-min">
                  <Input
                    id="rv-min"
                    type="number"
                    min={1}
                    max={5}
                    dir="ltr"
                    value={filters.minRating}
                    onChange={(event) => void load({ minRating: event.target.value })}
                  />
                </FilterField>
                <FilterField label={t('maxRating')} htmlFor="rv-max">
                  <Input
                    id="rv-max"
                    type="number"
                    min={1}
                    max={5}
                    dir="ltr"
                    value={filters.maxRating}
                    onChange={(event) => void load({ maxRating: event.target.value })}
                  />
                </FilterField>
              </div>

              <div className="md:col-span-2">
                {/* The picker is a multi-select; the filter is one product, so
                    the latest choice replaces the previous one. */}
                <ProductPicker
                  label={t('product')}
                  selectedIds={filters.productId ? [filters.productId] : []}
                  onChange={(ids) => void load({ productId: ids.at(-1) ?? '' })}
                />
              </div>
            </div>
          </FilterBar>

          <div className="flex flex-wrap items-center justify-between gap-3">
            {items.length > 0 && !loading ? (
              <label className="flex items-center gap-2 text-xs text-ink-muted">
                <Checkbox
                  label={t('reviewsSelectAll')}
                  checked={allShown}
                  onChange={() => setSelected(allShown ? new Set() : new Set(items.map((review) => review.id)))}
                />
                {t('reviewsSelectAll')}
              </label>
            ) : null}
            <p className="text-xs text-ink-muted">{t('moderationNote')}</p>
          </div>
        </ListToolbar>

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading ? (
          <div className="space-y-3 p-5">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState title={t('noReviews')} body={t('noReviewsHint')} icon={<CheckIcon />} />
        ) : (
          <TableScroll>
            <ul className="flex flex-col">
              {items.map((review) => (
                <ReviewCard
                  key={review.id}
                  review={review}
                  selected={selected.has(review.id)}
                  onToggle={() => toggle(review.id)}
                />
              ))}
            </ul>
          </TableScroll>
        )}

        <LoadMore visible={Boolean(nextCursor)} loading={loadingMore} onClick={() => void loadMore()} />
      </ListCard>

      {chosen.length > 0 ? (
        // The selection's own toolbar, floating over the list as on the orders
        // list — a card that moves under the pointer gets the click meant for
        // its neighbour. Inverted, so it reads in either theme.
        <div className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-4">
          <div
            role="toolbar"
            aria-label={t('selectedCount', { count: count(chosen.length, locale) })}
            className="pointer-events-auto flex max-w-full flex-wrap items-center gap-1.5 rounded-full bg-ink px-3 py-2 text-surface shadow-float"
          >
            <span className="px-2 text-sm font-medium">{t('selectedCount', { count: count(chosen.length, locale) })}</span>
            <span className="h-4 w-px bg-surface/25" aria-hidden />
            {chosen.some((review) => review.status !== 'APPROVED') ? (
              <BarButton onClick={() => setBulkTarget('APPROVED')} disabled={bulkSaving}>
                {t('reviewsPublishSelected')}
              </BarButton>
            ) : null}
            {chosen.some((review) => review.status !== 'REJECTED') ? (
              <BarButton onClick={() => setBulkTarget('REJECTED')} disabled={bulkSaving}>
                {t('reviewsRejectSelected')}
              </BarButton>
            ) : null}
            <BarButton onClick={() => setSelected(new Set())}>{t('clearSelection')}</BarButton>
          </div>
        </div>
      ) : null}

      {/* Neither decision deletes anything, but both change what the store
          shows and the ratings it shows — so a batch says so first. */}
      <ConfirmDialog
        open={bulkTarget !== null}
        title={
          bulkTarget === 'APPROVED'
            ? t('reviewsPublishTitle', { count: count(toChange.length, locale) })
            : t('reviewsRejectTitle', { count: count(toChange.length, locale) })
        }
        body={bulkTarget === 'APPROVED' ? t('reviewsPublishBody') : t('reviewsRejectBody')}
        confirmLabel={bulkTarget === 'APPROVED' ? t('publish') : t('reject')}
        cancelLabel={t('cancel')}
        tone={bulkTarget === 'APPROVED' ? 'primary' : 'danger'}
        busy={bulkSaving}
        onConfirm={() => void applyBulk()}
        onCancel={() => setBulkTarget(null)}
      />
    </Page>
  );
}

/** One review: the product it is about, the reviewer's words, the store's reply, and the decision. */
function ReviewCard({ review, selected, onToggle }: { review: ReviewDto; selected: boolean; onToggle: () => void }) {
  const moderate = useReviewsStore((state) => state.moderate);
  const busy = useReviewsStore((state) => state.bulkSaving || state.busyIds.includes(review.id));
  const mayOpenProducts = useAuthStore((state) => can(state.user, 'products:read'));
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  const status = STATUSES.find((entry) => entry.value === review.status);

  const product = (
    <>
      <Thumb src={mediaUrl(review.product.imageUrl)} alt="" size="xs" />
      <span className="min-w-0 truncate">{review.product.name}</span>
    </>
  );

  return (
    <li className={cx('flex gap-3 border-b border-line p-5', selected && 'bg-surface-sunken')}>
      <Checkbox
        className="mt-2 shrink-0"
        label={t('reviewSelect', { name: review.authorName })}
        checked={selected}
        onChange={onToggle}
      />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {mayOpenProducts ? (
            <Link
              to={`/products/${review.product.id}`}
              className="flex min-w-0 items-center gap-2 text-sm font-semibold text-ink hover:text-brand-600"
            >
              {product}
            </Link>
          ) : (
            <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-ink">{product}</span>
          )}
          <Pill tone={status?.tone ?? 'neutral'}>{t(status?.key ?? 'pendingQueue')}</Pill>
          <span className="ms-auto text-xs text-ink-muted">{date(review.createdAt, locale)}</span>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {/* A rating bar reads left to right in both languages: it is a
              scale, not text. */}
          <span aria-hidden dir="ltr" className="text-sm text-warning">
            {'★'.repeat(review.rating)}
            <span className="text-line">{'★'.repeat(Math.max(0, 5 - review.rating))}</span>
          </span>
          <span className="sr-only">
            {t('rating')}: {count(review.rating, locale)}
          </span>

          <span className="text-sm font-medium text-ink">{review.authorName}</span>

          {review.verifiedPurchase ? <Pill tone="success">{t('verifiedPurchase')}</Pill> : null}
        </div>

        {review.title ? <p className="mt-2 font-medium text-ink">{review.title}</p> : null}
        <p className="mt-1 whitespace-pre-line text-sm text-ink-muted">{review.body}</p>

        {/* What the reviewer made, where from, and how readers took it (R8). */}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-ink-muted">
          {review.usage ? <Pill tone="neutral">{review.usage.label}</Pill> : null}
          {review.city ? <span>{review.city}</span> : null}
          {review.helpfulCount > 0 ? (
            <span>{t('helpfulVotes', { count: count(review.helpfulCount, locale) })}</span>
          ) : null}
        </div>

        <ReplyEditor review={review} busy={busy} />

        <div className="mt-4 flex gap-2">
          {review.status !== 'APPROVED' ? (
            <Button variant="secondary" disabled={busy} onClick={() => void moderate(review.id, 'APPROVED')}>
              {t('publish')}
            </Button>
          ) : null}
          {review.status !== 'REJECTED' ? (
            <Button variant="ghost" disabled={busy} onClick={() => void moderate(review.id, 'REJECTED')}>
              {t('reject')}
            </Button>
          ) : null}
        </div>
      </div>
    </li>
  );
}

/**
 * The store's public answer to one review (R8). Written by a person — nothing
 * replies automatically — and shown under the review with its date.
 */
function ReplyEditor({ review, busy }: { review: ReviewDto; busy: boolean }) {
  const reply = useReviewsStore((state) => state.reply);
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const [draft, setDraft] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  if (draft !== null) {
    return (
      <div className="mt-3 flex flex-col gap-2">
        <Textarea
          rows={3}
          maxLength={2000}
          value={draft}
          aria-label={t('storeReply')}
          onChange={(event) => setDraft(event.target.value)}
        />
        <p className="text-xs text-ink-muted">{t('storeReplyHint')}</p>
        <div className="flex gap-2">
          <Button
            disabled={busy || draft.trim().length < 2}
            onClick={async () => {
              if (await reply(review.id, draft.trim())) setDraft(null);
            }}
          >
            {busy ? t('saving') : t('save')}
          </Button>
          <Button variant="ghost" onClick={() => setDraft(null)}>
            {t('cancel')}
          </Button>
        </div>
      </div>
    );
  }

  if (review.reply) {
    return (
      <div className="mt-3 border-s-2 border-line ps-3">
        <p className="text-xs font-semibold text-ink">
          {t('storeReply')}
          <span className="ms-2 font-normal text-ink-muted">{date(review.reply.at, locale)}</span>
        </p>
        <p className="mt-1 whitespace-pre-line text-sm text-ink-muted">{review.reply.body}</p>
        <div className="mt-1 flex gap-2">
          <Button variant="ghost" disabled={busy} onClick={() => setDraft(review.reply?.body ?? '')}>
            {t('editReply')}
          </Button>
          <Button variant="ghost" disabled={busy} onClick={() => setRemoving(true)}>
            {t('removeReply')}
          </Button>
        </div>

        <ConfirmDialog
          open={removing}
          title={t('reviewRemoveReplyTitle')}
          body={t('reviewRemoveReplyBody')}
          confirmLabel={t('removeReply')}
          cancelLabel={t('cancel')}
          busy={busy}
          onConfirm={async () => {
            await reply(review.id, null);
            setRemoving(false);
          }}
          onCancel={() => setRemoving(false)}
        />
      </div>
    );
  }

  return (
    <Button variant="ghost" className="mt-2" onClick={() => setDraft('')}>
      {t('replyAsStore')}
    </Button>
  );
}

/** A button on the floating selection bar — light on the inverted bar, in either theme. */
function BarButton({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="min-h-8 rounded-full px-3 text-xs font-medium text-surface transition-colors hover:bg-surface/15 disabled:opacity-50"
    >
      {children}
    </button>
  );
}
